<script setup lang="ts">
// Shown after the server refused to text the number on file (reason otp_destination_blocked, emailFallback: true).
const CODE_LENGTH = 6;
const TICK_MS = 1000;

const emit = defineEmits<{
  (e: 'verified'): void;
}>();

const { t } = useI18n();
const { toErrorMessage } = useErrorMessage();
const { mutateAsync: sendCode, isPending: isSending } = useSendPhoneEmailFallback();
const { mutateAsync: verifyCode, isPending: isVerifying } = useVerifyPhoneEmailFallback();

const maskedEmail = ref<string | null>(null);
const code = ref('');
const errorMessage = ref<string | null>(null);
const resendIn = ref(0);
let resendInterval: ReturnType<typeof setInterval> | undefined;

const canVerify = computed(() => code.value.length === CODE_LENGTH && !isVerifying.value);

const startResendTimer = (resendAvailableAt: string) => {
  clearInterval(resendInterval);
  resendIn.value = Math.max(Math.ceil((Date.parse(resendAvailableAt) - Date.now()) / TICK_MS), 0);
  resendInterval = setInterval(() => {
    resendIn.value -= 1;
    if (resendIn.value <= 0) clearInterval(resendInterval);
  }, TICK_MS);
};

const send = async () => {
  errorMessage.value = null;
  try {
    const { data } = await sendCode();
    maskedEmail.value = data.maskedEmail;
    code.value = '';
    startResendTimer(data.resendAvailableAt);
  } catch (error: unknown) {
    errorMessage.value = toErrorMessage(error, t('profile.phoneEmailFallback.genericError'));
  }
};

const submit = async () => {
  if (!canVerify.value) return;
  errorMessage.value = null;
  try {
    await verifyCode(code.value);
    emit('verified');
  } catch (error: unknown) {
    code.value = '';
    errorMessage.value = toErrorMessage(error, t('profile.phoneEmailFallback.genericError'));
  }
};

const onCodeInput = (event: Event) => {
  errorMessage.value = null;
  const target = event.target as HTMLInputElement;
  code.value = target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
  target.value = code.value;
  if (code.value.length === CODE_LENGTH) submit();
};

onBeforeUnmount(() => clearInterval(resendInterval));
</script>

<template>
  <div
    class="flex flex-col gap-3 text-start"
    data-testid="phone-email-fallback"
  >
    <template v-if="!maskedEmail">
      <h3 class="text-lg font-semibold text-black-100">{{ t('profile.phoneEmailFallback.title') }}</h3>
      <p class="text-sm text-black-70">{{ t('errors.otp.destinationBlocked') }}</p>
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-success px-5 text-sm font-bold text-white transition-colors hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isSending"
        data-testid="phone-email-fallback-send"
        @click="send"
      >
        {{ t('profile.phoneEmailFallback.send') }}
      </button>
    </template>

    <template v-else>
      <h3 class="text-lg font-semibold text-black-100">{{ t('profile.phoneEmailFallback.codeTitle') }}</h3>
      <p class="text-sm text-black-70">
        {{ t('profile.phoneEmailFallback.sentTo', { email: maskedEmail }) }}
      </p>
      <input
        :value="code"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        dir="ltr"
        :maxlength="CODE_LENGTH"
        :aria-label="t('profile.phoneEmailFallback.codeLabel')"
        class="h-14 w-full rounded-lg border-2 border-black-10 bg-white text-center text-xl font-bold tracking-[0.3em] text-black-100 outline-none focus:border-success"
        data-testid="phone-email-fallback-code"
        @input="onCodeInput"
      />
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-success px-5 text-sm font-bold text-white transition-colors hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="!canVerify"
        data-testid="phone-email-fallback-verify"
        @click="submit"
      >
        {{ isVerifying ? t('profile.phoneEmailFallback.verifying') : t('profile.phoneEmailFallback.verify') }}
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-sm font-semibold text-black-80 transition-colors hover:bg-black-05 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="resendIn > 0 || isSending"
        @click="send"
      >
        {{
          resendIn > 0
            ? t('profile.phoneEmailFallback.resendIn', { seconds: resendIn })
            : t('profile.phoneEmailFallback.resend')
        }}
      </button>
      <p class="text-xs text-black-60">{{ t('profile.phoneEmailFallback.proofNote') }}</p>
    </template>

    <div
      v-if="errorMessage"
      role="alert"
      class="rounded-lg bg-error-100/10 px-3 py-2 text-center text-sm font-semibold text-error-100"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>
